/* eslint-disable eqeqeq */
import { useEffect } from 'react';
import { useGetMasterListByUrlQuery } from 'app/services/ahuComponentFormsApi';
import { handleTabToNext } from '../../formKit';
import { ahuBoardBus } from '../../../bus/ahuBoardBus';
import { ACCESSORY_FORM_CONFIGS, UDA_COLLECTION_URLS } from '../accessoryFormConfigs';
import { useAccessoryForm } from '../useAccessoryForm';
import type { AccessoryFormProps } from '../AccessoryFormHost';

/**
 * Phase 6e2b-1 — USER DEFINED ACCESSORY form (accessories/
 * user_defined_accessory/{form.js 73, form.tmpl 479}, read in full).
 *  - withPosition:false, no mounting at all;
 *  - collections: UDAType/UDASupplyedBy/UDAAirStreamData/Voltages/PowerBy
 *    (master URLs — params.js:7-23) + UDA pricing (udc.pricing_method.js:9);
 *  - row-label rewrite "UD : <name>" (uda_name_id change + render —
 *    §49.6:858-866; emitted through update:accessories:tag:onboard so
 *    TabAccessories owns the DOM);
 *  - electrical sublist read-only until the selected UDAType has
 *    electrical_power; voltage unset gates power/fla/powered_by rows;
 *  - uda_name_id 3/4 → .fla-row non-clickable;
 *  - pricing ('priceing' [sic] config key; tmpl input data-subtab="pricing")
 *    gated non-clickable when supplied_by_id != 1; per-method cost/price
 *    field-name swap (2 → cost_*_each, 3 → price_*_each) — parity 330
 *    price/cost mutual exclusion is the per-method field swap here;
 *  - weight totals = each × qty (render fixup §49.6:812-817);
 *  - remarks input data-subtab="performance" quirk (state under 'remarks').
 *  - boot: `update:accessories:tag:onboard` fired when uda_name set
 *    (form.js:64).
 */
const config = ACCESSORY_FORM_CONFIGS.user_defined_accessory;

const Row = ({
  label,
  unit,
  children,
  rightSideClass,
  rowClass,
  unitClass,
}: {
  label: React.ReactNode;
  unit?: string;
  children: React.ReactNode;
  rightSideClass?: string;
  /** extra classes on the each-row div (J7: the Season row's non-clickable
   * gate + the season-group-summer/winter markers). */
  rowClass?: string;
  /** extra classes on the unit span — `temp` for the °F rows, which is what
   * common.css:21834 `.temp:before { content: "o" }` hangs the degree ring off
   * (form.tmpl:383,405,433,455,480,502 `class="right unit temp"`). */
  unitClass?: string;
}) => (
  <div className={`col-xs-12 each-row${rowClass ? ` ${rowClass}` : ''}`}>
    <div className="row">
      <div className="col-xs-6">
        <div className="col-xs-12 accessory_label-part">
          {label}
          {unit && <span className={`right unit${unitClass ? ` ${unitClass}` : ''}`}>{unit}</span>}
        </div>
      </div>
      <div className={`col-xs-6 right-side${rightSideClass ? ` ${rightSideClass}` : ''}`}>
        <div className="row accessory_value-part">{children}</div>
      </div>
    </div>
  </div>
);

const UdaForm = ({ row, section, tunnel, expanded }: AccessoryFormProps) => {
  const api = useAccessoryForm({
    config,
    row,
    section,
    tunnel,
    expanded,
    labeledSubtabs: ['electrical', 'performance', 'pricing', 'remarks'],
  });
  const { data: udaTypes } = useGetMasterListByUrlQuery(UDA_COLLECTION_URLS.udaTypes);
  const { data: supplyedBy } = useGetMasterListByUrlQuery(UDA_COLLECTION_URLS.supplyedBy);
  const { data: airStreams } = useGetMasterListByUrlQuery(UDA_COLLECTION_URLS.airStreams);
  const { data: voltages } = useGetMasterListByUrlQuery(UDA_COLLECTION_URLS.voltages);
  const { data: powerBy } = useGetMasterListByUrlQuery(UDA_COLLECTION_URLS.powerBy);
  const { data: udaPricing } = useGetMasterListByUrlQuery(UDA_COLLECTION_URLS.udaPricing);
  const { model } = api;

  // boot tag echo (form.js:64) — row label "UD : <name>".
  useEffect(() => {
    if (!model) return;
    const udaName = model.get('uda_name');
    if (udaName) {
      ahuBoardBus.emit('update:accessories:tag:onboard', { id: row.id, tag: String(udaName) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  if (api.loading || !model) {
    return <div className="preloader dotted-preloader active" />;
  }
  const cid = row.id;
  const udaNameId = model.get('uda_name_id');
  const selectedType = (udaTypes ?? []).find((t) => t.id == udaNameId);
  const hasElectrical = !!selectedType?.electrical_power;
  const voltageSet = !!model.get('voltage_id');
  const flaDisabled = udaNameId == 3 || udaNameId == 4;

  // ---- Dev-sync J7 — UDA seasons (uda_season split, legacy commits
  // 5197cc8927 + 69f7605e55 + c61adbff2a) ----------------------------------
  // is_air_uda — only electric_heater/humidifier get a live Season select
  // (uda form.js dataGetter:24-33); every other type keeps the row
  // non-clickable and the single bare condition pair.
  const typeValue = String((selectedType as { value?: unknown } | undefined)?.value ?? '');
  const isAirUda = typeValue === 'electric_heater' || typeValue === 'humidifier';
  // Electric Heater gets a SINGLE-option Season list — the final legacy list
  // is [Winter] (form.js:41-47; the adjacent "only ever runs in Summer"
  // comment is stale — the code and the accessory_view auto-sync both say
  // winter, and code wins).
  const isWinterOnly = typeValue === 'electric_heater';
  const udaSeasons = isWinterOnly
    ? [{ value: 'winter', name: 'Winter' }]
    : [
        { value: 'summer', name: 'Summer' },
        { value: 'winter', name: 'Winter' },
        { value: 'both', name: 'Summer & Winter' },
      ];
  const seasonRaw = model.get('uda_season');
  // Legacy render-time self-heal (accessory_view.js:878-885): an electric
  // heater whose stored uda_season isn't 'winter' gets coerced — legacy
  // model.set()s DURING render (an autosaved write-on-open). ★ Deviation:
  // the migration's zero-writes-on-open rule wins — the coercion here is
  // DISPLAY-level (select + groups render winter); the model heals on the
  // user's next uda_name_id action via the commitModelField ride-along.
  const udaSeason =
    udaNameId == 3 && seasonRaw !== 'winter' ? 'winter' : ((seasonRaw as string | null) ?? '');
  /** one season-split condition row (form.tmpl:378-398 grammar). */
  const seasonTempRow = (label: string, sea: 'summer' | 'winter', pair: 'ea' | 'la') => (
    <Row label={label} unit="F" unitClass="temp" rowClass={`season-group-${sea}`}>
      <div className="col-xs-6">
        {textInput(`${pair}db_temp_${sea}`, { formatter: 'decimal', min: -50, max: 150 })}
      </div>
      <div className="col-xs-6">
        {textInput(`${pair}wb_temp_${sea}`, { formatter: 'decimal', min: -50, max: 150 })}
      </div>
    </Row>
  );
  const suppliedBy1 = model.get('supplied_by_id') == 1;
  const pricingMethod = Number(model.get('pricing_method_id'));
  const qty = Number(model.get('qty')) || 0;
  const shippingTotal = model.get('shipping_weight_each')
    ? Number(model.get('shipping_weight_each')) * qty
    : model.get('shipping_weight_total');
  const operatingTotal = model.get('operating_weight_each')
    ? Number(model.get('operating_weight_each')) * qty
    : model.get('operating_weight_total');

  const textInput = (
    name: string,
    opts: {
      formatter?: 'decimal' | 'integer';
      min?: number;
      max?: number;
      maxLength?: number;
      placeholder?: string;
      readOnly?: boolean;
      extraClass?: string;
      value?: unknown;
      style?: React.CSSProperties;
    } = {}
  ) => (
    <input
      type="text"
      autoComplete="off"
      data-formatter_type={opts.formatter}
      data-min={opts.min}
      data-max={opts.max}
      maxLength={opts.maxLength}
      placeholder={opts.placeholder}
      className={`${opts.formatter ? '' : 'textarea '}model_field${opts.extraClass ? ` ${opts.extraClass}` : ''}`}
      name={name}
      style={opts.style}
      readOnly={opts.readOnly}
      defaultValue={String((opts.value !== undefined ? opts.value : model.get(name)) ?? '')}
      key={`${name}_${String((opts.value !== undefined ? opts.value : model.get(name)) ?? '')}`}
      onBlur={(e) => {
        if (opts.readOnly) return;
        api.commitModelField(
          name,
          e.target.value,
          opts.formatter ? { min: opts.min, max: opts.max, formatter: opts.formatter } : undefined
        );
      }}
    />
  );

  /**
   * §49.6:1253-1260 — picking an Accessory type rewrites the row label to
   * "UD : <name>" (and the toolbar tooltip) the moment the select changes.
   * Legacy writes both DOM nodes by hand; here the row label goes through
   * update:accessories:tag:onboard so TabAccessories stays the owner of that
   * markup, and only the toolbar span is written directly (it is an
   * un-owned `<span className="accessory_info" />` in CanvasToolbar).
   * Like legacy this is display-only — nothing persists the name into `tag`,
   * so a reload puts the label back to "UD" + the real tag on both stacks.
   */
  const rewriteUdaRowLabel = (udaName: string) => {
    ahuBoardBus.emit('update:accessories:tag:onboard', { id: row.id, tag: udaName });
    const info = `: UD ${udaName ? `: ${udaName}` : ''}`;
    document
      .querySelectorAll<HTMLElement>('#canvas_toolbar #accessories-ul span.accessory_info')
      .forEach((el) => {
        el.innerHTML = info;
      });
  };

  const selectInput = (
    name: string,
    options: Array<Record<string, unknown>>,
    opts: { hideNone?: boolean; dataElectrical?: boolean } = {}
  ) => (
    <select
      name={name}
      className="model_field"
      value={model.get(name) != null ? String(model.get(name)) : 'None'}
      onChange={(e) => {
        if (name === 'uda_name_id') {
          rewriteUdaRowLabel(e.target.value !== 'None' ? e.target.selectedOptions[0]?.text ?? '' : '');
        }
        api.commitModelField(name, e.target.value);
      }}
    >
      {!opts.hideNone && <option value="None">- -</option>}
      {options.map((o) => (
        <option
          key={String(o.id)}
          value={String(o.id)}
          data-electrical={opts.dataElectrical ? String(o.electrical_power ?? '') : undefined}
        >
          {String(o.name ?? '')}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <div className="form-heading">
        <div className="ahu-header-btns sync-result" />
      </div>
      <form ref={(el) => { api.formRef.current = el; }} onKeyDown={handleTabToNext}>
        <ul className="inputs-group sublist accessory uda">
          {/* properties (unlabeled first subtab; div-sublist grammar). */}
          <li>
            <input
              id={`uda_${cid}_properties`}
              type="checkbox"
              data-subtab="properties"
              checked={!!api.subtabs.properties}
              onChange={(e) => api.toggleSubtab('properties', e.target.checked)}
            />
            <div className="col-xs-12 sublist sublist_with-padding max-height-auto">
              <Row label="Accessory">
                <div className="col-xs-12">
                  {selectInput('uda_name_id', udaTypes ?? [], { dataElectrical: true })}
                </div>
              </Row>
              <Row label="Quantity">
                <div className="col-xs-12">
                  {textInput('qty', { formatter: 'integer', min: 0, max: 100, extraClass: ' text-left calc_weight' })}
                </div>
              </Row>
              <Row label="Description">
                <div className="col-xs-12">{textInput('description', { maxLength: 30 })}</div>
              </Row>
              <Row label="Manufacturer">
                <div className="col-xs-12">{textInput('uda_mfg', { maxLength: 30 })}</div>
              </Row>
              <Row label="Model">
                <div className="col-xs-12">{textInput('model', { maxLength: 30 })}</div>
              </Row>
              <Row label="Mfg's Part Number">
                <div className="col-xs-12">{textInput('mpn', { maxLength: 30 })}</div>
              </Row>
              <Row label="Width x Height x Length" unit="in">
                <div className="col-xs-4">{textInput('width', { formatter: 'decimal', min: 0, max: 144 })}</div>
                <div className="col-xs-4">{textInput('height', { formatter: 'decimal', min: 0, max: 144 })}</div>
                <div className="col-xs-4">{textInput('length', { formatter: 'decimal', min: 0, max: 144 })}</div>
              </Row>
              <Row label="Supplied by">
                <div className="col-xs-12 select-input">
                  {selectInput('supplied_by_id', supplyedBy ?? [], { hideNone: true })}
                </div>
              </Row>
              <Row label="Shipping Weight" unit="lb">
                <div className="col-xs-6">
                  {textInput('shipping_weight_each', { formatter: 'integer', min: 0, max: 100000, extraClass: ' calc_weight' })}
                </div>
                <div className="col-xs-6">
                  {textInput('shipping_weight_total', { formatter: 'integer', readOnly: true, value: shippingTotal })}
                </div>
              </Row>
              <Row label="Operating Weight" unit="lb">
                <div className="col-xs-6">
                  {textInput('operating_weight_each', { formatter: 'integer', min: 0, max: 100000, extraClass: ' calc_weight' })}
                </div>
                <div className="col-xs-6">
                  {textInput('operating_weight_total', { formatter: 'integer', readOnly: true, value: operatingTotal })}
                </div>
              </Row>
            </div>
          </li>

          {/* electrical */}
          <li>
            <input
              id={`uda_${cid}_electrical`}
              type="checkbox"
              data-subtab="electrical"
              checked={!!api.subtabs.electrical}
              onChange={(e) => api.toggleSubtab('electrical', e.target.checked)}
            />
            <label htmlFor={`uda_${cid}_electrical`}>
              Electrical
              <i className="icon-add" />
            </label>
            <div
              className={`col-xs-12 sublist sublist_with-padding electrical-sublist${
                hasElectrical ? '' : ' read-only-data'
              }`}
            >
              <div className="col-xs-12 each-row">
                <div className="row">
                  <div className="col-xs-6">
                    <div className="col-xs-12 accessory_label-part">Voltage</div>
                  </div>
                  <div className="col-xs-6 right-side">
                    <div className="row accessory_value-part">
                      <div className="col-xs-12 select-input">
                        {selectInput('voltage_id', voltages ?? [])}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-6">
                    <div className="col-xs-12 accessory_label-part">
                      Power
                      <span className="right unit">KW</span>
                    </div>
                  </div>
                  <div className={`col-xs-6 right-side${!voltageSet ? ' non-clickable' : ''}`}>
                    <div className="row accessory_value-part">
                      <div className="col-xs-12">
                        {textInput('power_w', { formatter: 'decimal', min: 0, max: 5000 })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`row fla-row${flaDisabled ? ' non-clickable' : ''}`}>
                  <div className="col-xs-6">
                    <div className="col-xs-12 accessory_label-part">
                      Full Load Amperage
                      <span className="right unit">Amp</span>
                    </div>
                  </div>
                  <div className={`col-xs-6 right-side${!voltageSet ? ' non-clickable' : ''}`}>
                    <div className="row accessory_value-part">
                      <div className="col-xs-12">
                        {textInput('fla', { formatter: 'decimal', min: 0, max: 1000 })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-xs-6">
                    <div className="col-xs-12 accessory_label-part">Powered By</div>
                  </div>
                  <div className={`col-xs-6 right-side${!voltageSet ? ' non-clickable' : ''}`}>
                    <div className="row accessory_value-part">
                      <div className="col-xs-12 select-input">
                        {selectInput('powered_by_id', powerBy ?? [])}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>

          {/* performance */}
          <li className="group-with-include-checkbox">
            <input
              id={`uda_${cid}_performance`}
              type="checkbox"
              data-subtab="performance"
              checked={!!api.subtabs.performance}
              onChange={(e) => api.toggleSubtab('performance', e.target.checked)}
            />
            <label htmlFor={`uda_${cid}_performance`}>
              Performance
              <i className="icon-add" />
            </label>
            {/* J7 — data-season attr + the Season row + season-split
                condition groups (form.tmpl:298-478). */}
            <div className="col-xs-12 sublist sublist_with-padding" data-season={udaSeason || undefined}>
              <Row label="Season" rowClass={isAirUda ? undefined : 'non-clickable'}>
                <div className="col-xs-12 select-input">
                  {/* String-valued options, hardcoded in the legacy view (NOT
                      MasterAHUSeason — no D1 dependency); '' commits null via
                      commitModelField's ''→null rule. */}
                  <select
                    name="uda_season"
                    className="model_field"
                    value={udaSeason}
                    onChange={(e) => api.commitModelField('uda_season', e.target.value)}
                  >
                    <option value="" className="hide">
                      - -
                    </option>
                    {udaSeasons.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Row>
              <Row
                label={
                  <>
                    <span className="required">Air Stream</span> &amp; Flow
                  </>
                }
                unit="CFM"
              >
                <div className="col-xs-6 select-input">{selectInput('air_stream_id', airStreams ?? [])}</div>
                <div className="col-xs-6">
                  {textInput('airflow_actual', { formatter: 'integer', min: 0, max: 400000 })}
                </div>
              </Row>
              <Row label="Face Velocity | APD" unit="fpm">
                <div className="col-xs-6">
                  {textInput('face_velocity_actual', { formatter: 'integer', min: 0, max: 1200 })}
                </div>
                <div className="col-xs-6">
                  <span className="unit" style={{ marginTop: 2 }}>
                    in WC
                  </span>
                  {/* legacy inline max-width:69px (form.tmpl:345) — without it
                      the input wraps under the "in WC" unit (QA batch 8 #4). */}
                  {textInput('apd', { formatter: 'decimal', min: 0, max: 50, style: { maxWidth: 69 } })}
                </div>
              </Row>
              {udaSeason ? (
                <>
                  {/* season set → per-season groups; 'both' shows BOTH
                      (form.tmpl:373-423 summer / :424-473 winter). */}
                  {udaSeason !== 'winter' && (
                    <>
                      <div className="col-xs-12 each-row season-group-summer">
                        <div className="col-xs-12 accessory_label-part" style={{ fontWeight: 700 }}>
                          Summer
                        </div>
                      </div>
                      {seasonTempRow('EADB | EAWB', 'summer', 'ea')}
                      {seasonTempRow('LADB | LAWB', 'summer', 'la')}
                    </>
                  )}
                  {udaSeason !== 'summer' && (
                    <>
                      <div className="col-xs-12 each-row season-group-winter">
                        <div className="col-xs-12 accessory_label-part" style={{ fontWeight: 700 }}>
                          Winter
                        </div>
                      </div>
                      {seasonTempRow('EADB | EAWB', 'winter', 'ea')}
                      {seasonTempRow('LADB | LAWB', 'winter', 'la')}
                    </>
                  )}
                </>
              ) : (
                <>
                  {/* no season → the pre-split bare pair (form.tmpl:475-522;
                      every non-air UDA type lives here, same as before
                      uda_season existed — models_user_defined_accessory.py:87-89). */}
                  <Row label="EADB | EAWB" unit="F" unitClass="temp">
                    <div className="col-xs-6">{textInput('eadb_temp', { formatter: 'decimal', min: -50, max: 150 })}</div>
                    <div className="col-xs-6">{textInput('eawb_temp', { formatter: 'decimal', min: -50, max: 150 })}</div>
                  </Row>
                  <Row label="LADB | LAWB" unit="F" unitClass="temp">
                    <div className="col-xs-6">{textInput('ladb_temp', { formatter: 'decimal', min: -50, max: 150 })}</div>
                    <div className="col-xs-6">{textInput('lawb_temp', { formatter: 'decimal', min: -50, max: 150 })}</div>
                  </Row>
                </>
              )}
            </div>
          </li>

          {/* pricing ('priceing' [sic] config key; tmpl data-subtab="pricing"). */}
          <li className="group-with-include-checkbox">
            <input
              id={`uda_${cid}_pricing`}
              type="checkbox"
              data-subtab="pricing"
              checked={!!api.subtabs.pricing}
              onChange={(e) => api.toggleSubtab('pricing', e.target.checked)}
            />
            <label htmlFor={`uda_${cid}_pricing`}>
              Pricing
              <i className="icon-add" />
            </label>
            <div
              className={`col-xs-12 sublist sublist_with-padding${!suppliedBy1 ? ' non-clickable' : ''}`}
            >
              <Row label="Pricing Method">
                <div className="col-xs-12 select-input">
                  {selectInput('pricing_method_id', suppliedBy1 ? udaPricing ?? [] : [], { hideNone: true })}
                </div>
              </Row>
              {pricingMethod != 1 && (
                <Row label="Mtl | Freight" unit="US$">
                  <div className="col-xs-6">
                    {textInput(pricingMethod == 3 ? 'price_mtl_each' : 'cost_mtl_each', {
                      formatter: 'decimal',
                      min: 1,
                      max: 1000000,
                      extraClass: ' text-left medium-input',
                    })}
                  </div>
                  <div className="col-xs-6">
                    {textInput(pricingMethod == 3 ? 'price_freight_each' : 'cost_freight_each', {
                      formatter: 'decimal',
                      min: 1,
                      max: 1000000,
                      extraClass: ' text-left medium-input',
                    })}
                  </div>
                </Row>
              )}
            </div>
          </li>

          {/* remarks — tmpl data-subtab="performance" quirk; state 'remarks'. */}
          <li className="group-with-include-checkbox">
            <input
              id={`uda_${cid}_remarks`}
              type="checkbox"
              data-subtab="performance"
              checked={!!api.subtabs.remarks}
              onChange={(e) => api.toggleSubtab('remarks', e.target.checked)}
            />
            <label htmlFor={`uda_${cid}_remarks`}>
              Remarks
              <i className="icon-add" />
              <span className="short_remarks">{String(model.get('remarks') ?? '')}</span>
            </label>
            <div className="col-xs-12 sublist sublist_with-padding">
              <div className="col-xs-12 each-row">
                <div className="row">
                  <div className="col-xs-12">
                    {textInput('remarks', {
                      maxLength: 300,
                      placeholder: '300 characters maximum',
                    })}
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </form>
      <div id={`popup_accessory_mounting_${row.id}`} className="popup-wrapper location-popup">
        <div className="popup-content popup-content-animation" />
      </div>
    </>
  );
};

export default UdaForm;
